import { UserIcon } from "lucide-react";

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";

import { AssigneeMember } from "@/features/tickets/types";
import { cn } from "@/shared/lib/utils/cn";

interface Props {
  members: AssigneeMember[];
  selected: string | null;
  onChange: (id: string | null) => void;
  disabled?: boolean;
}

export function AssigneeSelector({ members, selected, onChange, disabled }: Props) {
  return (
    <div className="flex flex-wrap gap-2">
      <button
        type="button"
        onClick={() => onChange(null)}
        disabled={disabled}
        className={cn(
          "group flex items-center gap-2 border py-1.5 pl-1.5 pr-3 text-left transition-colors",
          selected === null ? "border-primary bg-primary/5 text-foreground" : "border-border bg-card text-muted-foreground hover:border-border hover:bg-accent hover:text-foreground",
          disabled && "pointer-events-none opacity-50"
        )}
      >
        <div className="flex size-6 shrink-0 items-center justify-center bg-muted">
          <UserIcon className="size-3 text-muted-foreground" />
        </div>
        <div>
          <p className="text-xs font-medium leading-none">Unassigned</p>
          <p className="mt-0.5 text-[10px] text-muted-foreground">No assignee</p>
        </div>
      </button>
      {members.map((member) => {
        const isSelected = selected === member.id;
        return (
          <button
            key={member.id}
            type="button"
            onClick={() => onChange(member.id)}
            disabled={disabled}
            className={cn(
              "group flex items-center gap-2 border py-1.5 pl-1.5 pr-3 text-left transition-colors",
              isSelected ? "border-primary bg-primary/5 text-foreground" : "border-border bg-card text-muted-foreground hover:border-border hover:bg-accent hover:text-foreground",
              disabled && "pointer-events-none opacity-50"
            )}
          >
            <Avatar size="sm">
              <AvatarImage src={member.avatarUrl} alt={member.name} />
              <AvatarFallback className="text-[10px]">{member.initials}</AvatarFallback>
            </Avatar>
            <div>
              <p className="text-xs font-medium leading-none">{member.name}</p>
              <p className="mt-0.5 text-[10px] text-muted-foreground">{member.role}</p>
            </div>
          </button>
        );
      })}
    </div>
  );
}
